import { create } from '@bufbuild/protobuf';
import useCloudService from '@grpc/composables/useCloudService';
import useCloudServiceV2 from '@grpc/composables/useCloudServiceV2';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import type { Flavour } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import { AuthenticationType, type BasicImage } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import type { Node, RescueMode } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import type { Datacenter } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import {
  ChangeNodeBillingPeriodRequestSchema,
  ChangeNodeRescueModeRequestSchema,
  CreateNodeRequestSchema,
  DestroyNodeRequestSchema,
  GetNodeRequestSchema,
  GetNodeSearchOptionsRequestSchema,
  GetRemoteConsoleAccessRequestSchema,
  ReinstallNodeRequestSchema,
  UpdateNodeRequestSchema,
} from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import type {
  ChangeNodeBillingPeriodResponse,
  ChangeNodeRescueModeResponse,
  CreateNodeResponse,
  DestroyNodeResponse,
  GetNodeResponse,
  GetNodeSearchOptionsResponse,
  GetRemoteConsoleAccessResponse,
  ReinstallNodeResponse,
  UpdateNodeResponse,
} from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import {
  ServerPowerState,
  ServerProvisioningState,
} from 'gpcore-grpc/gpcore/api/cloud/v1/server_pb';
import { PaginationRequestSchema } from 'gpcore-grpc/gpcore/type/v1/pagination_pb';
import type { SearchRequest } from 'gpcore-grpc/gpcore/type/v1/search_pb';
import { defineStore } from 'pinia';
import { computed, reactive, toRefs } from 'vue';
import { useProjectStore } from './project';
import type { ListNodesResponse } from 'gpcore-grpc/gpcore/api/cloud/v2/responses_pb';
import { ListNodesRequestSchema } from 'gpcore-grpc/gpcore/api/cloud/v2/requests_pb';

export interface NodeStoreState {
  nodes: Array<Node>;
  activeNode?: string;
}

export interface Authentication {
  type: AuthenticationType;

  password?: string;
  sshKeys?: Array<string>;
}

export interface Settings {
  userData: string;
}

export interface CreateNodes {
  flavour?: Flavour;
  billingPeriod?: BillingPeriod;

  datacenter?: Datacenter;
  image?: BasicImage;

  authentication?: Authentication;
  settings?: Settings;

  nodes: Array<string>;
  networks?: Array<string>;
}

export interface ReinstallNode {
  id: string;
  image?: BasicImage;
  authentication?: Authentication;
  settings?: Settings;
  fqdn?: string;
}

export interface PowerAction {
  node: Node;
  action: ServerPowerState;
}

export interface ChangeBillingPeriod {
  node: Node;
  billingPeriod: BillingPeriod;
}

export const useNodeStore = defineStore('node', () => {
  const cloudService = useCloudService();
  const cloudServiceV2 = useCloudServiceV2();
  const projectStore = useProjectStore();

  const state = reactive<NodeStoreState>({
    nodes: new Array<Node>(),
    activeNode: undefined,
  });

  const node = computed(() => state.nodes.find((node) => node.id === state.activeNode));

  const nodeActionsAllowed = computed<boolean>(() => {
    if (state.activeNode === undefined) return false;
    const node = state.nodes.find((n) => n.id === state.activeNode);
    if (!node || node.isManaged) {
      return false;
    }

    return (
      node.status === ServerProvisioningState.ACTIVE ||
      node.status === ServerProvisioningState.AVAILABLE ||
      node.status === ServerProvisioningState.RESCUE ||
      node.status === ServerProvisioningState.RESCUE_FAIL ||
      node.status === ServerProvisioningState.RESCUING ||
      node.status === ServerProvisioningState.RESCUE_WAIT ||
      node.status === ServerProvisioningState.UNRESCUING
    );
  });

  // migrated mutations
  const setNode = (node: Node) => {
    const existingIndex = state.nodes.findIndex((s) => s.id === node.id);
    if (existingIndex > -1) {
      state.nodes[existingIndex] = node;
    } else {
      state.nodes.unshift(node);
    }
  };

  const deleteNode = (node: Node) => {
    const existingIndex = state.nodes.findIndex((s) => s.id === node.id);
    if (existingIndex > -1) {
      if (state.activeNode === node.id) {
        state.activeNode = undefined;
      }

      state.nodes.splice(existingIndex, 1);
    }
  };

  // migrated actions
  const reloadNodes = async (
    page: number,
    searchString?: string,
    search?: SearchRequest,
  ): Promise<ListNodesResponse> => {
    const request = create(ListNodesRequestSchema);
    const pagination = create(PaginationRequestSchema);

    pagination.page = page;
    request.pagination = pagination;
    if (projectStore.project == null) {
      console.error(
        'projectStore.project is null. I recommend take a look into router guard to ensure that the project is set correctly.',
      );
      throw new Error(
        'This project is unknown. Please either switch to another project or create a new one.',
      );
    }

    request.id = projectStore.project.id;

    if (search) {
      request.extendedSearch = search;
    }

    if (searchString && searchString !== '') {
      request.search = searchString;
    }

    const { response, error } = await cloudServiceV2.executeApiCall('listNodes', request);
    if (error != null) throw error;
    state.nodes = response.nodes;

    return response;
  };

  const getExtendedSearchOptions = async (): Promise<GetNodeSearchOptionsResponse> => {
    const request = create(GetNodeSearchOptionsRequestSchema);
    request.id = projectStore.project.id;

    const { response, error } = await cloudService.executeApiCall('getNodeSearchOptions', request);
    if (error != null) throw error;

    return response;
  };

  const getNode = async (id: string): Promise<GetNodeResponse> => {
    state.activeNode = null;

    const request = create(GetNodeRequestSchema);
    request.id = id;
    request.projectId = projectStore.project.id;

    const { response, error } = await cloudService.executeApiCall('getNode', request);
    if (error != null) throw error;
    state.activeNode = response.node.id;
    setNode(response.node);

    return response;
  };

  const createNodes = async (nodeToCreate: CreateNodes): Promise<CreateNodeResponse> => {
    const request = create(CreateNodeRequestSchema);
    request.projectId = projectStore.project.id;
    request.datacenterId = nodeToCreate.datacenter.id;
    request.flavourId = nodeToCreate.flavour.id;
    request.imageId = nodeToCreate.image.id;
    request.fqdns = nodeToCreate.nodes;
    request.billingPeriod = nodeToCreate.billingPeriod;

    if (nodeToCreate.settings?.userData) {
      request.userData = nodeToCreate.settings.userData;
    }

    if (nodeToCreate.authentication.type === AuthenticationType.PASSWORD) {
      request.password = nodeToCreate.authentication!.password!;
    } else {
      request.sshKeyIds = nodeToCreate.authentication!.sshKeys!;
    }

    const { response, error } = await cloudService.executeApiCall('createNode', request);
    if (error != null) throw error;
    response.nodes.forEach((node) => {
      setNode(node);
    });
    return response;
  };

  const reinstallNode = async (reinstall: ReinstallNode): Promise<ReinstallNodeResponse> => {
    const request = create(ReinstallNodeRequestSchema);
    request.id = reinstall.id;
    request.projectId = projectStore.project.id;
    request.fqdn = reinstall.fqdn;
    request.imageId = reinstall.image?.id;

    if (reinstall.settings?.userData) {
      request.userData = reinstall.settings.userData;
    }

    if (reinstall.authentication.type === AuthenticationType.PASSWORD) {
      request.password = reinstall.authentication!.password!;
    } else {
      request.sshKeyIds = reinstall.authentication!.sshKeys!;
    }

    const { response, error } = await cloudService.executeApiCall('reinstallNode', request);
    if (error != null) throw error;

    setNode(response.node);

    return response;
  };

  const changeNodeBillingPeriod = async (
    req: ChangeBillingPeriod,
  ): Promise<ChangeNodeBillingPeriodResponse> => {
    const request = create(ChangeNodeBillingPeriodRequestSchema);
    request.id = req.node.id;
    request.projectId = projectStore.project.id;
    request.billingPeriod = req.billingPeriod;

    const { response, error } = await cloudService.executeApiCall(
      'changeNodeBillingPeriod',
      request,
    );
    if (error != null) throw error;

    setNode(response.node);

    return response;
  };

  const updateNode = async (node: Node): Promise<UpdateNodeResponse> => {
    const request = create(UpdateNodeRequestSchema);
    request.id = node.id;
    request.projectId = projectStore.project.id;
    request.fqdn = node.fqdn;

    request.tags = {};
    Object.entries(node.tags).forEach(([k, v]) => {
      request.tags[k] = v;
    });

    const { response, error } = await cloudService.executeApiCall('updateNode', request);
    if (error != null) throw error;

    setNode(response.node);

    return response;
  };

  const toggleRescueMode = async (mode: RescueMode): Promise<ChangeNodeRescueModeResponse> => {
    const request = create(ChangeNodeRescueModeRequestSchema);
    request.id = node.value.id;
    request.projectId = projectStore.project.id;
    request.rescueMode = mode;

    const { response, error } = await cloudService.executeApiCall('changeNodeRescueMode', request);
    if (error != null) throw error;

    // setNode(response.getNode());

    return response;
  };

  const destroyNode = async (
    node: Node,
    options?: { onDestroyed?: () => void | Promise<void> },
  ): Promise<DestroyNodeResponse> => {
    const request = create(DestroyNodeRequestSchema);
    request.id = node.id;
    request.projectId = projectStore.project.id;

    const { response, error } = await cloudService.executeApiCall('destroyNode', request);
    if (error != null) throw error;

    // Allow the caller to react (e.g. navigate away) before the node is removed from
    // the store. Removing the active node nulls the `node` getter, which collapses
    // node-detail views gated on `node != null` and blanks the screen.
    await options?.onDestroyed?.();

    deleteNode(node);

    return response;
  };

  const getRemoteConsoleAccess = async (node: Node): Promise<GetRemoteConsoleAccessResponse> => {
    const request = create(GetRemoteConsoleAccessRequestSchema);
    // TODO PLATFORM_MANAGEMENT: remove hardcoded netbox id
    request.id = node.id;

    const { response, error } = await cloudService.executeApiCall(
      'getRemoteConsoleAccess',
      request,
    );
    if (error != null) throw error;

    return response;
  };

  return {
    ...toRefs(state as NodeStoreState),
    node,
    nodeActionsAllowed,
    setNode,
    reloadNodes,
    getExtendedSearchOptions,
    getNode,
    createNodes,
    reinstallNode,
    changeNodeBillingPeriod,
    getRemoteConsoleAccess,
    updateNode,
    toggleRescueMode,
    destroyNode,
  };
});

export default useNodeStore;
